import QRCode from "qrcode";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";

interface TotpStatus {
  mfa_confirmed: boolean;
  mfa_required: boolean;
  recovery_codes_remaining: number;
}

export function TwoFactorPage() {
  const { refreshUser } = useAuth();

  const [status, setStatus] = useState<TotpStatus | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    const { data } = await api.get<TotpStatus>("/auth/mfa/status");
    setStatus(data);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await api.get<TotpStatus>("/auth/mfa/status");
        if (!cancelled) {
          setStatus(data);
        }
      } catch {
        if (!cancelled) {
          setError("Nije moguće dohvatiti stanje dvofaktorske prijave.");
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const run = async (action: () => Promise<void>, fallback: string) => {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught, fallback));
    } finally {
      setBusy(false);
    }
  };

  const startEnrolment = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const { data } = await api.post<{
        secret: string;
        provisioning_uri: string;
      }>("/auth/mfa/setup", { password });
      setPassword("");
      setSecret(data.secret);
      setQr(await QRCode.toDataURL(data.provisioning_uri, { margin: 1 }));
    }, "Uključivanje nije uspjelo.");
  };

  const confirmEnrolment = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const { data } = await api.post<{ codes: string[] }>("/auth/mfa/confirm", {
        code,
      });
      setCodes(data.codes);
      setSecret(null);
      setQr(null);
      setCode("");
      await loadStatus();
      await refreshUser();
    }, "Kod nije prihvaćen.");
  };

  const regenerate = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const { data } = await api.post<{ codes: string[] }>(
        "/auth/mfa/recovery-codes/regenerate",
        { password },
      );
      setCodes(data.codes);
      setPassword("");
      await loadStatus();
    }, "Izdavanje novih kodova nije uspjelo.");
  };

  const disable = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      await api.post("/auth/mfa/disable", { password });
      setPassword("");
      setCodes(null);
      await loadStatus();
      await refreshUser();
    }, "Isključivanje nije uspjelo.");
  };

  return (
    <main className="mx-auto flex max-w-md flex-col gap-4 px-4 py-10">
      <Link to="/" className="text-sm text-muted-foreground underline">
        ← Natrag
      </Link>

      {error && <Alert variant="error">{error}</Alert>}
      {status?.mfa_required && <Alert>Dvofaktorska prijava je obvezna za ovaj račun.</Alert>}

      {codes && <BackupCodeList codes={codes} onDone={() => setCodes(null)} />}

      {!codes && status?.mfa_confirmed === false && !secret && (
        <Card>
          <CardHeader>
            <CardTitle>Dvofaktorska prijava</CardTitle>
            <CardDescription>
              Uz lozinku traži i kod s vašeg telefona. Preporučeno za sve koji
              vide osobne podatke ili plaće.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="flex flex-col gap-3" onSubmit={startEnrolment}>
              <Label htmlFor="setup-password">Potvrdite lozinkom</Label>
              <Input id="setup-password" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} />
              <Button type="submit" disabled={busy}>
                {busy ? "Priprema…" : "Uključi dvofaktorsku prijavu"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {secret && (
        <Card>
          <CardHeader>
            <CardTitle>Poveži aplikaciju</CardTitle>
            <CardDescription>
              Skenirajte kod aplikacijom za autentifikaciju, pa upišite
              šesteroznamenkasti broj koji vam pokaže.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* Scanning this with the phone camera opens otpauth:// as a web
                address and fails. It has to be scanned from inside an
                authenticator app, which is not obvious to anybody who has not
                done it before - so the screen says so before showing the code,
                not after. */}
            <Alert>
              <p className="font-medium">
                Skenirajte iz aplikacije za autentifikaciju, ne kamerom
                telefona.
              </p>
              <p className="mt-2">
                Kamera će ovaj kod pokušati otvoriti kao web adresu i neće
                uspjeti. Otvorite aplikaciju, odaberite dodavanje novog računa
                i skenirajte odande.
              </p>
            </Alert>

            <div className="flex flex-col gap-2">
              <Label>Aplikacije koje rade</Label>
              <ul className="flex flex-col gap-2 text-sm">
                <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium">Microsoft Authenticator</span>
                  <StoreLinks
                    apple="https://apps.apple.com/app/id983156458"
                    android="https://play.google.com/store/apps/details?id=com.azure.authenticator"
                  />
                </li>
                <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="font-medium">Google Authenticator</span>
                  <StoreLinks
                    apple="https://apps.apple.com/app/id388497605"
                    android="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                  />
                </li>
              </ul>
              <p className="text-sm text-muted-foreground">
                Ako već koristite neku drugu aplikaciju za kodove, i ona će
                raditi.
              </p>
            </div>

            {qr && (
              <img
                src={qr}
                alt="QR kod za povezivanje aplikacije za autentifikaciju"
                className="mx-auto h-48 w-48"
              />
            )}

            <div className="flex flex-col gap-2">
              <Label htmlFor="secret">
                Ne možete skenirati? Upišite ovaj ključ u aplikaciju ručno
              </Label>
              <code
                id="secret"
                className="select-all break-all rounded-md border border-border bg-muted px-3 py-3 font-mono text-base tracking-wider"
              >
                {secret}
              </code>
              <p className="text-sm text-muted-foreground">
                U aplikaciji odaberite unos ključa umjesto skeniranja.
              </p>
            </div>

            <form className="flex flex-col gap-3" onSubmit={confirmEnrolment}>
              <Label htmlFor="code">Kod iz aplikacije</Label>
              <Input
                id="code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
              <Button type="submit" disabled={busy}>
                {busy ? "Provjera…" : "Potvrdi i uključi"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {!codes && status?.mfa_confirmed && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Dvofaktorska prijava je uključena</CardTitle>
              <CardDescription>
                Preostalo rezervnih kodova: {status.recovery_codes_remaining}.
                {status.recovery_codes_remaining <= 3 &&
                  " Izdajte nove dok ih još imate."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="flex flex-col gap-3" onSubmit={regenerate}>
                <Label htmlFor="regen-password">
                  Potvrdite lozinkom da izdate nove rezervne kodove
                </Label>
                <Input
                  id="regen-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <Button type="submit" variant="outline" disabled={busy}>
                  Izdaj nove rezervne kodove
                </Button>
              </form>
            </CardContent>
          </Card>

          {!status.mfa_required && <Card>
            <CardHeader>
              <CardTitle className="text-lg">Isključivanje</CardTitle>
              <CardDescription>
                Bez dvofaktorske prijave račun čuva samo lozinka.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="flex flex-col gap-3" onSubmit={disable}>
                <Label htmlFor="off-password">Potvrdite lozinkom</Label>
                <Input
                  id="off-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <Button type="submit" variant="destructive" disabled={busy}>
                  Isključi dvofaktorsku prijavu
                </Button>
              </form>
            </CardContent>
          </Card>}
        </>
      )}
    </main>
  );
}

/**
 * Recovery codes, shown once.
 *
 * There is no endpoint that returns these again, so the screen says so plainly
 * and makes the user confirm before it clears them.
 */
function BackupCodeList({
  codes,
  onDone,
}: {
  codes: string[];
  onDone: () => void;
}) {
  const [confirmed, setConfirmed] = useState(false);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rezervni kodovi</CardTitle>
        <CardDescription>
          Zapišite ih ili ispišite. Svaki vrijedi jednom i služi za prijavu
          ako izgubite telefon.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Alert variant="error">
          Ovo je jedini put da se kodovi prikazuju. Nakon zatvaranja više ih
          nije moguće vidjeti.
        </Alert>

        <ul className="grid grid-cols-2 gap-2">
          {codes.map((code) => (
            <li
              key={code}
              className="rounded-md bg-muted px-3 py-2 text-center font-mono text-sm tracking-wider"
            >
              {code}
            </li>
          ))}
        </ul>

        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-5 w-5 min-h-0"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          <span>Spremio sam kodove na sigurno mjesto.</span>
        </label>

        <Button onClick={onDone} disabled={!confirmed}>
          Gotovo
        </Button>
      </CardContent>
    </Card>
  );
}


/**
 * App store links.
 *
 * Opened in a new tab so a half-finished enrolment is not lost by navigating
 * away from it; `noreferrer` because there is no reason to tell the store
 * where the visitor came from.
 */
function StoreLinks({ apple, android }: { apple: string; android: string }) {
  return (
    <span className="flex gap-2 text-sm">
      <a
        href={apple}
        target="_blank"
        rel="noreferrer noopener"
        className="underline"
      >
        iPhone
      </a>
      <span className="text-muted-foreground" aria-hidden="true">
        ·
      </span>
      <a
        href={android}
        target="_blank"
        rel="noreferrer noopener"
        className="underline"
      >
        Android
      </a>
    </span>
  );
}
